// How a rider's questions sit on the card: which boxes share a row. Both ways
// of filling in a rider's questions use it, so they cannot drift apart: the
// widget's checkout (ParticipantDetails, .vt-field-pair / .vt-q-row) and the
// admin pay-link page (/pay/[token], its inline grids). Pure: no React, no
// DOM; the spec (e2e/question-layout.spec.ts) drives it directly. Layout
// only: the questions, their words and the answers saved are the tenant's,
// exactly as configured. (Which options of a tick list span the whole row is
// option-columns.ts, wideOptions.)
//
//   * pairFields: which neighbouring questions share a row (first name +
//     surname, mobile + email), by the preset's KEYS only (FIELD_PAIRS), never
//     a label. Whether a pair really sits side by side is the page's call
//     (two columns only when both fit).
//   * layoutFields: FIELD_PAIRS stay pairs (pairFields). Besides them, a run
//     of short number boxes (the trail-ride form's age, height and weight)
//     shares one row, up to ROW_MAX per row. A question asked only for some
//     answers (show_when) that sits inside such a run is shown directly below
//     the row, so the row never forms or splits while the guest types.
//     Whether the boxes really sit side by side is the page's call.
//   * displayOrder: the questions in the order the card shows them, so the
//     "what is missing" check names (and takes the guest to) the first thing
//     missing ON SCREEN.
//   * labelTail: a label's last character, which a page keeps on one line
//     with the required mark, so a narrow column never leaves the "*" alone.

/** The pairs, by the preset's keys (the trail-ride preset's rider questions). */
export const FIELD_PAIRS: readonly (readonly [string, string])[] = [
  ["first_name", "surname"],
  ["mobile", "email"],
];

type LayoutField = { key: string; type: string; sensitive?: boolean | null; show_when?: unknown };

/**
 * A one-line box that can share a row: plain text (a name, a number, an
 * email), always shown. A question shown only for some answers never pairs,
 * so a pair cannot form or split while the guest types (that would move a
 * box, and the focus, out from under them).
 */
function pairable(f: LayoutField): boolean {
  return f.type === "text" && f.sensitive !== true && !f.show_when;
}

function isPair(a: LayoutField, b: LayoutField): boolean {
  return FIELD_PAIRS.some(
    ([x, y]) => (a.key === x && b.key === y) || (a.key === y && b.key === x),
  );
}

/**
 * The questions of one form (ALL of them, shown or not), in order, with each
 * pair of neighbours that make one of FIELD_PAIRS (either order) grouped as a
 * two-item array. Both must be plain, always-shown, non-sensitive text boxes:
 * a sensitive question opens a privacy line above itself, and a list, a long
 * answer or a tick belongs on its own row. Every other question stays on its
 * own, in the same place, so the tab order is unchanged: left then right, top
 * to bottom. The caller skips the singles that are hidden.
 */
export function pairFields<F extends LayoutField>(fields: readonly F[]): (F | [F, F])[] {
  const out: (F | [F, F])[] = [];
  for (let i = 0; i < fields.length; i++) {
    const a = fields[i]!;
    const b = fields[i + 1];
    if (b && pairable(a) && pairable(b) && isPair(a, b)) {
      out.push([a, b]);
      i++;
    } else out.push(a);
  }
  return out;
}

/** One place on the card: a question on its own, a FIELD_PAIRS pair, or a row. */
export type LayoutEntry<F> =
  | { kind: "one"; field: F }
  | { kind: "pair"; fields: [F, F] }
  | { kind: "row"; fields: F[] };

/** At most this many boxes share a row. */
export const ROW_MAX = 3;

/**
 * A box that can share a row: a number (short to type, short to show),
 * always shown, not private. Text boxes do not: a name or an address can be
 * long, and the names and contacts already pair by their keys. A sensitive
 * question opens a privacy line above itself, so it stands alone.
 */
function rowable(f: LayoutField): boolean {
  return f.type === "number" && f.sensitive !== true && !f.show_when;
}

/** n boxes (n >= 2) in rows of at most ROW_MAX, as even as can be, fuller rows first: 4 is 2 + 2, 5 is 3 + 2, 7 is 3 + 2 + 2. */
export function rowSizes(n: number): number[] {
  if (n < 2) return n === 1 ? [1] : [];
  const rows = Math.ceil(n / ROW_MAX);
  const base = Math.floor(n / rows);
  const extra = n % rows;
  return Array.from({ length: rows }, (_, i) => base + (i < extra ? 1 : 0));
}

/**
 * The questions of one form (ALL of them, shown or not, so nothing moves as
 * answers show and hide questions), in the order the card shows them.
 *   * pairs: pairFields, unchanged.
 *   * rows: two or more rowable questions with nothing between them but
 *     questions asked only for some answers (show_when). Those conditional
 *     questions come directly below the row that holds the question they
 *     followed, in their configured order; one after the run's last box is
 *     not inside it and keeps its place. Anything else (a text box, a list,
 *     a private question that is always asked, a pair) ends the run.
 *   * everything else: on its own, in its configured place.
 * The card shows them in this order, so the tab order is the visual order:
 * left to right along a row, then down. The caller skips the singles that
 * are hidden (a row's and a pair's boxes are always shown).
 */
export function layoutFields<F extends LayoutField>(fields: readonly F[]): LayoutEntry<F>[] {
  const paired = pairFields(fields);
  const out: LayoutEntry<F>[] = [];
  let i = 0;
  while (i < paired.length) {
    const first = paired[i]!;
    if (Array.isArray(first)) {
      out.push({ kind: "pair", fields: first });
      i++;
      continue;
    }
    if (!rowable(first)) {
      out.push({ kind: "one", field: first });
      i++;
      continue;
    }
    // The run: rowable boxes, with the conditional questions between them
    // remembered against the box they followed.
    const boxes: F[] = [first];
    const below = new Map<F, F[]>();
    let waiting: F[] = [];
    let j = i + 1;
    for (; j < paired.length; j++) {
      const next = paired[j]!;
      if (Array.isArray(next)) break;
      if (rowable(next)) {
        if (waiting.length) below.set(boxes[boxes.length - 1]!, waiting);
        waiting = [];
        boxes.push(next);
      } else if (next.show_when) waiting.push(next);
      else break;
    }
    if (boxes.length < 2) {
      out.push({ kind: "one", field: first });
      i++;
      continue;
    }
    let at = 0;
    for (const size of rowSizes(boxes.length)) {
      const row = boxes.slice(at, at + size);
      at += size;
      out.push({ kind: "row", fields: row });
      for (const box of row) for (const q of below.get(box) ?? []) out.push({ kind: "one", field: q });
    }
    // Conditional questions after the run's last box keep their place.
    i = j - waiting.length;
  }
  return out;
}

/** The questions in the order the card shows them (the privacy lines are placed over this order). */
export function layoutOrder<F>(entries: readonly LayoutEntry<F>[]): F[] {
  return entries.flatMap((e) => (e.kind === "one" ? [e.field] : e.fields));
}

/**
 * The questions of one form in the order the card shows them (layoutFields,
 * flattened): a question asked only for some answers that sits inside a row
 * of number boxes comes after the row. Checking a rider in this order makes
 * the "what is missing" message (and, in the widget, the box Next focuses)
 * the first one missing from the top of the card (an empty Height in the row
 * above before a guardian's name below it), never one further down.
 */
export function displayOrder<F extends LayoutField>(fields: readonly F[]): F[] {
  return layoutOrder(layoutFields(fields));
}

/**
 * A question's label split so its required mark (" *", after a no-break
 * space) stays on the line of the label's last letter. `tail` is the label's
 * last character, whole (a letter with its accents, an emoji), `head` the
 * rest; the page renders head, then tail + mark in one nowrap span. Layout
 * only: head + tail is the label as configured (trailing spaces aside, which
 * a page never shows). Why: in a narrow column (a row's number boxes) a word
 * wider than the column breaks inside it (overflow-wrap: anywhere), and the
 * break falls at the last character that fits, which for "Handedness *" in
 * a 77px column is right before the "*": the mark alone on the next line.
 * With the last letter held to it, the word breaks one letter earlier and
 * the mark goes down with that letter.
 */
export function labelTail(label: string): { head: string; tail: string } {
  const text = label.replace(/\s+$/u, "");
  if (!text) return { head: "", tail: "" };
  let tail = "";
  const Segmenter = (Intl as { Segmenter?: typeof Intl.Segmenter }).Segmenter;
  if (Segmenter) {
    for (const part of new Segmenter(undefined, { granularity: "grapheme" }).segment(text)) tail = part.segment;
  } else {
    // A base character and the marks (accents) after it.
    tail = /\P{M}\p{M}*$/u.exec(text)?.[0] ?? text.slice(-1);
  }
  return { head: text.slice(0, text.length - tail.length), tail };
}
